import React, { useMemo } from "react";
import { format, parseISO } from "date-fns";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import CustomChip from "components/common/Chip";
import CustomTooltip from "components/common/Tooltip";

import { getCountryColor, getHolidayActualDate } from "components/Screens/HolidaySchedule/utils";

import "./HolidayItem.scss";

const TENTATIVE_TOOLTIP_TEXT =
  "This holiday follows the lunar calendar and depends on the sighting of the moon, so the exact date may shift by a day.";

function HolidayItem({ holidaysOnDate, onHolidayClick, upcomingDateKey }) {
  const firstHoliday = holidaysOnDate[0];
  const hasMultipleHolidays = true;

  const formattedDate = useMemo(() => {
    const actualDate = getHolidayActualDate(firstHoliday);
    return actualDate || parseISO(firstHoliday.startDate);
  }, [firstHoliday]);

  const upcomingDateMatch = useMemo(() => {
    if (!upcomingDateKey || !formattedDate) {
      return false;
    }
    return format(formattedDate, "yyyy-MM-dd") === upcomingDateKey;
  }, [formattedDate, upcomingDateKey]);

  const isUpcoming = upcomingDateMatch;

  return (
    <div className="holiday-item">
      <div
        className={`holiday-date-section ${hasMultipleHolidays ? "multiple-holidays" : ""} ${
          isUpcoming ? "upcoming-holiday" : ""
        }`}
      >
        <p className="heading-h5-semibold color-text-headings-primary">{format(formattedDate, "MMM dd")}</p>
        <p className="holiday-day para-body-s-regular color-text-heading-secondary">{format(formattedDate, "EEEE")}</p>
      </div>

      <div className="holiday-content">
        {holidaysOnDate.map((holiday) => (
          <React.Fragment key={holiday.id || `${holiday.startDate}-${holiday.name}-${holiday.region}`}>
            <div className="holiday-row" onClick={() => onHolidayClick(holiday)}>
              <div
                className="holiday-indicator"
                style={{
                  backgroundColor: getCountryColor(holiday.region),
                }}
              />

              <div className="holiday-details">
                <div className="holiday-name-row">
                  <p className="heading-h5-semibold">
                    {holiday.name}
                    {holiday.totalDays > 1 && ` (day ${holiday.dayNumber})`}
                  </p>
                  {holiday.isTentative && (
                    <CustomTooltip content={TENTATIVE_TOOLTIP_TEXT} direction="top">
                      <span>
                        <CustomChip label="Tentative" size="small" variant="filled" color="default" disabled />
                      </span>
                    </CustomTooltip>
                  )}
                </div>
                <p className="para-body-s-medium color-text-body-primary">
                  {Array.isArray(holiday.region) ? holiday.region.join(", ") : holiday.region}
                </p>
              </div>

              <div className="holiday-arrow">
                <ChevronRightIcon sx={{ color: "var(--color-icon-secondary)", fontSize: 24 }} />
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default HolidayItem;
